import { t } from "@lingui/macro";
import { CheckCircleIcon, HighlighterIcon, SparkleIcon } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Label,
  ScrollArea,
  Separator,
  Textarea,
} from "@reactive-resume/ui";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

type AiSection = {
  name: string;
  score: number;
  confidence: "low" | "medium" | "high";
  label: "fully_human" | "likely_ai" | "fully_ai";
  issues: {
    repeatedStructure: boolean;
    genericPhrases: boolean;
    unnaturalWording: boolean;
    lackSpecificity: boolean;
    excessiveBuzzwords: boolean;
  };
  evidence: string[];
  debug?: {
    burstinessScore: number;
    extractedFeatures?: any;
    sentenceAnalysis?: any[];
    burstinessAnalysis?: any;
    ensembleInputs?: any;
  };
};

type RepetitionRubricItem = {
  score: number;
  details?: string;
};

type RepetitionSection = {
  name: string;
  responsibilities: RepetitionRubricItem;
  duties: RepetitionRubricItem;
  skills: RepetitionRubricItem;
  operations: RepetitionRubricItem;
  repetition: {
    score: number;
    details: string;
    highlights: string[];
  };
};

type RepetitionChecker = {
  score: number;
  growthPattern: "excellent" | "good" | "average" | "poor" | "concerning";
  analysis: string;
  identicalPositions: string[];
  recommendations: string[];
  sections?: RepetitionSection[];
  positionProgression: {
    position: string;
    level: "entry" | "junior" | "mid" | "senior" | "lead" | "executive";
    uniqueness: number;
    issues: string[];
  }[];
};

type AtsCheckResult = {
  aiContentDetection: {
    overall: {
      score: number;
      confidence: "low" | "medium" | "high";
      label: "fully_human" | "likely_ai" | "fully_ai";
    };
    sections: AiSection[];
    summary: string;
  };
  atsScore: {
    score: number;
    matchedScore?: number;
    missingScore?: number;
    matchedKeywords: string[];
    missingKeywords: string[];
    suggestions: string[];
  };
  repetitionChecker: RepetitionChecker;
  overallScore: number;
};

export const AiDetectorSection = () => {
  const { toast } = useToast();
  const resume = useResumeStore((state) => state.resume);
  const frameRef = useBuilderStore((state) => state.frame.ref);

  const [checkMode, setCheckMode] = useState<"ai" | "ats">("ai");
  const [jobDescription, setJobDescription] = useState("");
  const [mainRequirements, setMainRequirements] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<AtsCheckResult | null>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isRewriting, setIsRewriting] = useState(false);

  const highlightAiText = () => {
    if (!frameRef?.contentWindow || !result?.aiContentDetection?.sections) return;

    // Collect different types of AI indicators with their respective colors
    const highlights = {
      aiSentences: [] as string[],      // Red - AI-styled sentences
      buzzwords: [] as string[],         // Orange - Buzzwords
      aiVerbs: [] as string[],           // Yellow - AI verbs
      vagueStatements: [] as string[],   // Purple - Vague statements
    };

    result.aiContentDetection.sections.forEach(section => {
      // 1. Collect AI-styled sentences from Few-Shot block
      if (section.debug?.sentenceAnalysis) {
        section.debug.sentenceAnalysis.forEach((sent: any) => {
          if (sent.isAiStyled && sent.text) {
            const cleanText = sent.text.trim();
            if (cleanText.length > 0) {
              highlights.aiSentences.push(cleanText);
            }
          }
        });
      }

      // 2. Collect linguistic features from CoT block
      if (section.debug?.extractedFeatures) {
        const features = section.debug.extractedFeatures;
        
        // Buzzwords
        if (features.buzzwords && Array.isArray(features.buzzwords)) {
          highlights.buzzwords.push(...features.buzzwords);
        }
        
        // AI Verbs
        if (features.aiVerbsFound && Array.isArray(features.aiVerbsFound)) {
          highlights.aiVerbs.push(...features.aiVerbsFound);
        }
        
        // Vague Statements
        if (features.vagueStatements && Array.isArray(features.vagueStatements)) {
          highlights.vagueStatements.push(...features.vagueStatements);
        }
      }
    });

    // Remove duplicates from each category
    highlights.aiSentences = [...new Set(highlights.aiSentences)];
    highlights.buzzwords = [...new Set(highlights.buzzwords)];
    highlights.aiVerbs = [...new Set(highlights.aiVerbs)];
    highlights.vagueStatements = [...new Set(highlights.vagueStatements)];

    // Count total highlights
    const totalHighlights = 
      highlights.aiSentences.length + 
      highlights.buzzwords.length + 
      highlights.aiVerbs.length + 
      highlights.vagueStatements.length;
    
    if (totalHighlights === 0) {
      toast({
        variant: "info",
        title: t`No AI indicators detected`,
      });
      return;
    }

    const message = {
      type: "HIGHLIGHT_AI_TEXT_MULTI",
      payload: highlights,
    };
    frameRef.contentWindow.postMessage(message, "*");
    setIsHighlighted(true);

    toast({
      variant: "success",
      title: t`${totalHighlights} AI indicators highlighted`,
      description: t`Red: AI sentences, Orange: Buzzwords, Yellow: AI verbs, Purple: Vague statements`,
    });
  };

  const clearHighlights = () => {
    if (!frameRef?.contentWindow) return;

    const message = { type: "CLEAR_HIGHLIGHTS" };
    frameRef.contentWindow.postMessage(message, "*");
    setIsHighlighted(false);
  };

  const rewriteAiSentences = async () => {
    if (!result?.aiContentDetection?.sections) return;

    // Collect all AI-styled sentences with their metadata from all sections
    const aiSentences: Array<{ 
      section: string; 
      sentence: string;
      flag: string;
      reason: string;
      confidence: number;
    }> = [];
    
    result.aiContentDetection.sections.forEach(section => {
      if (section.debug?.sentenceAnalysis) {
        section.debug.sentenceAnalysis.forEach((sent: any) => {
          if (sent.isAiStyled && sent.text) {
            aiSentences.push({
              section: section.name,
              sentence: sent.text.trim(),
              flag: sent.flag || "AI-styled",
              reason: sent.reason || "No specific reason provided",
              confidence: sent.confidence || 0.5
            });
          }
        });
      }
    });

    if (aiSentences.length === 0) {
      toast({
        variant: "info",
        title: t`No AI-styled sentences to rewrite`,
      });
      return;
    }

    setIsRewriting(true);
    try {
      const response = await axios.post("/ai/rewrite-sentences", {
        sentences: aiSentences,
        jobDescription: jobDescription || undefined,
        context: resume.data.basics.headline || "Professional resume"
      });

      const rewriteData = response.data;
      
      // Actually replace the sentences in the resume
      if (rewriteData.rewrittenSentences && rewriteData.rewrittenSentences.length > 0) {
        const data = resume.data;
        
        // Create a mapping of original to rewritten sentences
        const replacementMap = new Map<string, string>();
        rewriteData.rewrittenSentences.forEach((item: any) => {
          replacementMap.set(item.original.trim(), item.rewritten.trim());
        });

        // Replace in Summary section
        if (data.sections.summary.content) {
          let summaryContent = data.sections.summary.content;
          replacementMap.forEach((rewritten, original) => {
            summaryContent = summaryContent.replace(original, rewritten);
          });
          useResumeStore.getState().setValue("sections.summary.content", summaryContent);
        }

        // Replace in Experience section
        data.sections.experience.items.forEach((exp, idx) => {
          if (exp.summary) {
            let expSummary = exp.summary;
            replacementMap.forEach((rewritten, original) => {
              expSummary = expSummary.replace(original, rewritten);
            });
            useResumeStore.getState().setValue(`sections.experience.items.${idx}.summary`, expSummary);
          }
        });

        // Replace in Projects section
        if (data.sections.projects?.items) {
          data.sections.projects.items.forEach((proj: any, idx: number) => {
            if (proj.summary) {
              let projSummary = proj.summary;
              replacementMap.forEach((rewritten, original) => {
                projSummary = projSummary.replace(original, rewritten);
              });
              useResumeStore.getState().setValue(`sections.projects.items.${idx}.summary`, projSummary);
            }
          });
        }

        toast({
          variant: "success",
          title: t`${aiSentences.length} sentences rewritten and replaced`,
          description: t`Your resume has been updated with more human-like content`,
        });

        // Clear results to force re-check
        setResult(null);
        setIsHighlighted(false);
      }
    } catch (error) {
      toast({
        variant: "error",
        title: t`Failed to rewrite sentences`,
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsRewriting(false);
    }
  };

  const extractResumeText = () => {
    const data = resume.data;
    let text = "";

    text += `${data.basics.name}\n`;
    text += `${data.basics.headline}\n`;
    text += `${data.basics.email} | ${data.basics.phone}\n`;
    text += `${data.basics.location}\n\n`;

    if (data.sections.summary.content) {
      text += `Summary:\n${data.sections.summary.content}\n\n`;
    }

    if (data.sections.experience.items.length > 0) {
      text += "Experience:\n";
      data.sections.experience.items.forEach((exp) => {
        text += `${exp.position} at ${exp.company}\n`;
        text += `${exp.date} | ${exp.location}\n`;
        text += `${exp.summary}\n\n`;
      });
    }

    if (data.sections.education.items.length > 0) {
      text += "Education:\n";
      data.sections.education.items.forEach((edu) => {
        text += `${edu.studyType} in ${edu.area} from ${edu.institution}\n`;
        text += `${edu.date}\n\n`;
      });
    }

    if (data.sections.skills.items.length > 0) {
      text += "Skills:\n";
      data.sections.skills.items.forEach((skill) => {
        text += `${skill.name}: ${skill.keywords.join(", ")}\n`;
      });
    }

    return text;
  };

  const onCheck = async () => {

    // Clear previous results
    setResult(null);
    setIsHighlighted(false);

    setIsChecking(true);
    try {
      const resumeContent = extractResumeText();

      if (checkMode === "ai") {
        // AI Content Detection only - separate endpoint
        const aiResponse = await axios.post("/ai/ai-check", {
          resumeContent,
        });

        // Create result with only AI detection data
        setResult({
          aiContentDetection: aiResponse.data,
          atsScore: {
            score: 0,
            matchedKeywords: [],
            missingKeywords: [],
            suggestions: [],
          },
          repetitionChecker: {
            score: 0,
            growthPattern: "average" as const,
            analysis: "",
            identicalPositions: [],
            recommendations: [],
            sections: [],
            positionProgression: [],
          },
          overallScore: 100 - aiResponse.data.overall.score, // AI score (lower AI = higher overall)
        });
      } else {
        // ATS mode - use job description data from left sidebar
        const jobDescriptionSection = resume.data.sections.jobDescription;
        
        // Combine job description fields
        let combinedJobDescription = "";
        if (jobDescriptionSection?.description) {
          combinedJobDescription += `Job Description: ${jobDescriptionSection.description}\n\n`;
        }
        if (jobDescriptionSection?.responsibilities) {
          combinedJobDescription += `Responsibilities: ${jobDescriptionSection.responsibilities}\n\n`;
        }
        if (jobDescriptionSection?.impact) {
          combinedJobDescription += `Impact: ${jobDescriptionSection.impact}\n\n`;
        }

        // Use skills required as main requirements
        const mainRequirements = jobDescriptionSection?.skillsRequired || undefined;

        // Fallback to generic if no job description data is available
        const finalJobDescription = combinedJobDescription.trim() || "General ATS compatibility check";

        const response = await axios.post<AtsCheckResult>("/ai/ats-check", {
          resumeContent,
          jobDescription: finalJobDescription,
          mainRequirements,
        });

        setResult(response.data);
      }
    } catch (error) {
      toast({
        variant: "error",
        title: t`Failed to check resume`,
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsChecking(false);
    }
  };

  const getScoreBadge = (score: number) => {
    if (score >= 80) return "success";
    if (score >= 60) return "warning";
    return "error";
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <section id="ai-detector" className="grid gap-y-4">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparkleIcon size={20} />
          <h3 className="font-semibold">{t`AI & Resume Analysis`}</h3>
        </div>
      </header>

      <div className="space-y-4">
        {!result ? (
          <div className="space-y-3">
            {/* Card Buttons */}
            <div className="grid gap-3">
              {/* AI Detection Card */}
              <div 
                className={`
                  relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md
                  ${checkMode === "ai" 
                    ? "border-primary bg-primary/5 shadow-sm" 
                    : "border-border bg-card hover:border-primary/50"
                  }
                `}
                onClick={() => setCheckMode("ai")}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-md ${checkMode === "ai" ? "bg-primary/10" : "bg-secondary"}`}>
                    <SparkleIcon size={16} className={checkMode === "ai" ? "text-primary" : "text-muted-foreground"} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">{t`AI Detection Writing Style`}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t`Detect AI-generated content patterns and unnatural writing styles in your resume`}
                    </p>
                  </div>
                </div>
                {checkMode === "ai" && (
                  <div className="absolute top-2 right-2">
                    <CheckCircleIcon size={16} className="text-primary" />
                  </div>
                )}
              </div>

              {/* ATS Checker Card */}
              <div 
                className={`
                  relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md
                  ${checkMode === "ats" 
                    ? "border-primary bg-primary/5 shadow-sm" 
                    : "border-border bg-card hover:border-primary/50"
                  }
                `}
                onClick={() => setCheckMode("ats")}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-md ${checkMode === "ats" ? "bg-primary/10" : "bg-secondary"}`}>
                    <CheckCircleIcon size={16} className={checkMode === "ats" ? "text-primary" : "text-muted-foreground"} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">{t`ATS Checker & Pattern Detection`}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t`Analyze keyword matching, career progression, and detect repetition patterns`}
                    </p>
                  </div>
                </div>
                {checkMode === "ats" && (
                  <div className="absolute top-2 right-2">
                    <CheckCircleIcon size={16} className="text-primary" />
                  </div>
                )}
              </div>
            </div>

            {/* Current Mode Description */}
            <div className="rounded-md bg-secondary/30 p-3">
              <p className="text-xs text-muted-foreground">
                {checkMode === "ai" 
                  ? t`AI Detection analyzes your resume content for artificial writing patterns using advanced linguistic analysis.`
                  : t`ATS Checker analyzes keyword compatibility, career growth progression, and identifies repetitive patterns in experience.`
                }
              </p>
            </div>

            {/* Run Analysis Button */}
            <Button
              onClick={onCheck}
              disabled={isChecking}
              className="w-full"
              size="sm"
            >
              {checkMode === "ai" ? (
                <SparkleIcon size={14} className="mr-2" />
              ) : (
                <CheckCircleIcon size={14} className="mr-2" />
              )}
              {isChecking ? t`Analyzing...` : checkMode === "ai" ? t`Run AI Detection` : t`Run ATS Analysis`}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* AI Detection Results */}
            {checkMode === "ai" && (
              <>
            {/* Circular Meter */}
            <div className="rounded-lg border bg-secondary/50 p-4">
              <h4 className="mb-3 font-semibold text-center text-sm">{t`AI vs Human Ratio`}</h4>
              <div className="flex justify-center mb-3">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-200 dark:text-gray-700" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" strokeDasharray={`${(100 - result.aiContentDetection.overall.score) * 2.513} 251.3`} className="text-green-500 transition-all duration-1000" strokeLinecap="round" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" strokeDasharray={`${result.aiContentDetection.overall.score * 2.513} 251.3`} strokeDashoffset={`${-(100 - result.aiContentDetection.overall.score) * 2.513}`} className="text-red-500 transition-all duration-1000" strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-xl font-bold">{100 - result.aiContentDetection.overall.score}%</p>
                    <p className="text-[10px] text-muted-foreground">Human</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center gap-4 mb-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                  <span className="text-xs">{100 - result.aiContentDetection.overall.score}% Human</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                  <span className="text-xs">{result.aiContentDetection.overall.score}% AI</span>
                </div>
              </div>
              <div className="flex justify-center gap-2">
                <Badge variant={result.aiContentDetection.overall.label === "fully_human" ? "success" : result.aiContentDetection.overall.label === "likely_ai" ? "warning" : "error"}>
                  {result.aiContentDetection.overall.label.replace("_", " ").toUpperCase()}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {result.aiContentDetection.overall.confidence} confidence
                </Badge>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={isHighlighted ? "outline" : "primary"}
                className="w-full"
                size="sm"
                onClick={isHighlighted ? clearHighlights : highlightAiText}
              >
                <HighlighterIcon size={14} className="mr-1" />
                {isHighlighted ? t`Clear` : t`Highlight`}
              </Button>
              <Button
                variant="secondary"
                className="w-full"
                size="sm"
                onClick={rewriteAiSentences}
                disabled={isRewriting}
              >
                <SparkleIcon size={14} className="mr-1" />
                {isRewriting ? t`Rewriting...` : t`Rewrite AI`}
              </Button>
            </div>

            {/* Highlight Legend */}
            {isHighlighted && (
              <div className="rounded-lg border bg-secondary/30 p-3">
                <h4 className="text-xs font-semibold mb-2">{t`Highlight Legend`}</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgba(239, 68, 68, 0.5)", border: "1px solid rgba(239, 68, 68, 0.8)" }}></div>
                    <span>{t`AI Sentences`}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgba(249, 115, 22, 0.5)", border: "1px solid rgba(249, 115, 22, 0.8)" }}></div>
                    <span>{t`Buzzwords`}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgba(234, 179, 8, 0.5)", border: "1px solid rgba(234, 179, 8, 0.8)" }}></div>
                    <span>{t`AI Verbs`}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgba(168, 85, 247, 0.5)", border: "1px solid rgba(168, 85, 247, 0.8)" }}></div>
                    <span>{t`Vague Statements`}</span>
                  </div>
                </div>
              </div>
            )}

            <Separator />

            {/* Summary */}
            <div>
              <h4 className="mb-2 font-semibold text-sm">{t`Summary`}</h4>
              <p className="text-xs leading-relaxed text-muted-foreground">{result.aiContentDetection.summary}</p>
            </div>

            <Separator />

            {/* Section Analysis */}
            <div>
              <h4 className="mb-3 font-semibold text-sm">{t`Section Analysis`}</h4>
              <div className="space-y-4">
                {result.aiContentDetection.sections.map((section, idx) => (
                  <div key={idx} className="rounded-lg border p-3 space-y-3">
                    {/* Section Header */}
                    <div className="flex items-center justify-between">
                      <h5 className="font-semibold text-sm">{section.name}</h5>
                      <div className="flex items-center gap-1.5">
                        <Badge variant={section.label === "fully_human" ? "success" : section.label === "likely_ai" ? "warning" : "error"} className="text-xs">
                          {section.label.replace("_", " ").toUpperCase()}
                        </Badge>
                      </div>
                    </div>

                    {/* Mini Progress Bar */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-red-500 transition-all duration-500"
                          style={{ width: `${section.score}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium w-14 text-right">{100 - section.score}%</span>
                    </div>

                    {/* Analysis Summary */}
                    {section.debug && (
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="rounded bg-orange-50 dark:bg-orange-950/30 p-2">
                          <p className="text-[10px] text-muted-foreground">Burstiness</p>
                          <p className="text-xs font-semibold">{section.debug.burstinessScore}</p>
                        </div>
                        <div className="rounded bg-purple-50 dark:bg-purple-950/30 p-2">
                          <p className="text-[10px] text-muted-foreground">AI Sentences</p>
                          <p className="text-xs font-semibold">
                            {section.debug.sentenceAnalysis ? 
                              `${section.debug.sentenceAnalysis.filter((s: any) => s.isAiStyled).length}/${section.debug.sentenceAnalysis.length}` 
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Detailed Block Outputs */}
                    {section.debug && (
                      <details className="text-xs">
                        <summary className="cursor-pointer font-medium text-primary hover:underline">
                          View Detailed Analysis
                        </summary>
                        <div className="mt-3 space-y-3">
                          {/* CoT Block - Extracted Features */}
                          {section.debug.extractedFeatures && (
                            <div className="rounded border bg-blue-50/50 dark:bg-blue-950/20 p-2">
                              <h6 className="font-semibold mb-1.5 text-[11px]">Block 1: Linguistic Features</h6>
                              <div className="space-y-1 text-[10px]">
                                {section.debug.extractedFeatures.aiVerbsFound?.length > 0 && (
                                  <p><strong>AI Verbs:</strong> {section.debug.extractedFeatures.aiVerbsFound.join(", ")}</p>
                                )}
                                {section.debug.extractedFeatures.buzzwords?.length > 0 && (
                                  <p><strong>Buzzwords:</strong> {section.debug.extractedFeatures.buzzwords.join(", ")}</p>
                                )}
                                {section.debug.extractedFeatures.jargon?.length > 0 && (
                                  <p><strong>Jargon:</strong> {section.debug.extractedFeatures.jargon.join(", ")}</p>
                                )}
                                {section.debug.extractedFeatures.repetitiveStarters?.length > 0 && (
                                  <p><strong>Repetitive Starters:</strong> {section.debug.extractedFeatures.repetitiveStarters.join(", ")}</p>
                                )}
                                {section.debug.extractedFeatures.vagueStatements?.length > 0 && (
                                  <p><strong>Vague Statements:</strong> {section.debug.extractedFeatures.vagueStatements.length} found</p>
                                )}
                                {section.debug.extractedFeatures.specificMentions?.length > 0 && (
                                  <p><strong>Specific Mentions:</strong> {section.debug.extractedFeatures.specificMentions.join(", ")}</p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Few-Shot Block - Sentence Analysis */}
                          {section.debug.sentenceAnalysis && section.debug.sentenceAnalysis.length > 0 && (
                            <div className="rounded border bg-purple-50/50 dark:bg-purple-950/20 p-2">
                              <h6 className="font-semibold mb-1.5 text-[11px]">Block 2: Sentence Analysis</h6>
                              <p className="mb-2 text-[10px]">
                                <strong>Total:</strong> {section.debug.sentenceAnalysis.length} | 
                                <strong className="ml-2 text-red-600">AI:</strong> {section.debug.sentenceAnalysis.filter((s: any) => s.isAiStyled).length} | 
                                <strong className="ml-2 text-green-600">Human:</strong> {section.debug.sentenceAnalysis.filter((s: any) => !s.isAiStyled).length}
                              </p>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {section.debug.sentenceAnalysis.slice(0, 3).map((sent: any, sIdx: number) => (
                                  <div key={sIdx} className={`rounded p-1.5 ${sent.isAiStyled ? 'bg-red-100 dark:bg-red-950/40' : 'bg-green-100 dark:bg-green-950/40'}`}>
                                    <p className="font-medium text-[10px]">{sent.text}</p>
                                    <p className="text-[9px] mt-0.5">
                                      <strong>{sent.flag}</strong> - {sent.reason}
                                    </p>
                                  </div>
                                ))}
                                {section.debug.sentenceAnalysis.length > 3 && (
                                  <p className="text-center text-muted-foreground text-[10px]">+ {section.debug.sentenceAnalysis.length - 3} more</p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Burstiness Block */}
                          {section.debug.burstinessAnalysis && (
                            <div className="rounded border bg-orange-50/50 dark:bg-orange-950/20 p-2">
                              <h6 className="font-semibold mb-1.5 text-[11px]">Block 3: Burstiness</h6>
                              <div className="grid grid-cols-2 gap-1 text-[10px]">
                                <p><strong>Std Dev:</strong> {section.debug.burstinessAnalysis.standardDeviation}</p>
                                <p><strong>CV:</strong> {section.debug.burstinessAnalysis.coefficientOfVariation}</p>
                                <p><strong>Mean:</strong> {section.debug.burstinessAnalysis.meanLength}</p>
                                <p><strong>Range:</strong> {section.debug.burstinessAnalysis.range}</p>
                              </div>
                            </div>
                          )}

                          {/* Ensemble Inputs */}
                          {section.debug.ensembleInputs && (
                            <div className="rounded border bg-gray-50 dark:bg-gray-900/50 p-2">
                              <h6 className="font-semibold mb-1.5 text-[11px]">Ensemble Inputs</h6>
                              <div className="space-y-0.5 text-[10px]">
                                <p><strong>Burstiness:</strong> {section.debug.ensembleInputs.burstinessStdDev}</p>
                                <p><strong>Repetitions:</strong> {section.debug.ensembleInputs.repetitiveStartersCount}</p>
                                <p><strong>Vague:</strong> {section.debug.ensembleInputs.vagueBulletsCount}</p>
                                <p><strong>Jargon:</strong> {section.debug.ensembleInputs.technicalJargonCount}</p>
                                <p><strong>Flagged Ratio:</strong> {section.debug.ensembleInputs.flaggedRatio?.toFixed(2)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                    
                    {/* Issues */}
                    {Object.values(section.issues).some(v => v) && (
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(section.issues).map(([key, value]) => 
                          value && (
                            <Badge key={key} variant="warning" className="text-[10px]">
                              {key === "repeatedStructure" && "Repeated Structure"}
                              {key === "genericPhrases" && "Generic Phrases"}
                              {key === "unnaturalWording" && "Unnatural Wording"}
                              {key === "lackSpecificity" && "Lack Specificity"}
                              {key === "excessiveBuzzwords" && "Excessive Buzzwords"}
                            </Badge>
                          )
                        )}
                      </div>
                    )}

                    {/* Evidence phrases */}
                    {section.evidence.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-[10px] font-medium text-muted-foreground">Flagged Phrases:</p>
                        {section.evidence.map((phrase, pIdx) => (
                          <div key={pIdx} className="rounded bg-red-50 dark:bg-red-950/30 p-1.5 text-[10px]">
                            "{phrase}"
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            </>
            )}

            {/* ATS Results */}
            {checkMode === "ats" && (() => {
              const matchedCount = result.atsScore.matchedKeywords.length;
              const missingCount = result.atsScore.missingKeywords.length;
              const keywordTotal = matchedCount + missingCount;
              const matchedScore =
                result.atsScore.matchedScore ??
                (keywordTotal === 0 ? 0 : Math.round((matchedCount / keywordTotal) * 100));
              const missingScore =
                result.atsScore.missingScore ??
                (keywordTotal === 0 ? 0 : Math.round((missingCount / keywordTotal) * 100));
              const repetitionSections = result.repetitionChecker?.sections ?? [];
              const suggestions = [
                ...(result.atsScore.suggestions ?? []),
                ...(result.repetitionChecker?.recommendations ?? []),
              ].filter(Boolean);

              return (
              <div className="space-y-3">
                {/* 1. Missing Keywords score + list */}
                <div className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">{t`Missing Keywords`}</h4>
                    <span className={`font-bold text-sm ${getScoreColor(100 - missingScore)}`}>
                      {missingScore}%
                    </span>
                  </div>
                  {missingCount > 0 ? (
                    <ul className="space-y-1">
                      {result.atsScore.missingKeywords.map((keyword, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs">
                          <span className="mt-0.5 text-red-500">•</span>
                          <span>{keyword}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t`No missing keywords`}</p>
                  )}
                </div>

                {/* 2. Matched Keywords score only */}
                <div className="rounded-lg border p-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">{t`Matched Keywords`}</h4>
                    <span className={`font-bold text-sm ${getScoreColor(matchedScore)}`}>
                      {matchedScore}%
                    </span>
                  </div>
                </div>

                {/* 3. Repetition Pattern rubric per section */}
                <div className="rounded-lg border p-3 space-y-3">
                  <h4 className="font-semibold text-sm">{t`Repetition Pattern`}</h4>
                  {repetitionSections.length > 0 ? (
                    repetitionSections.map((section, idx) => {
                      const rubricRows = [
                        { label: t`Responsibilities Mentioned`, score: section.responsibilities?.score ?? 0, details: section.responsibilities?.details },
                        { label: t`Duties Mentioned`, score: section.duties?.score ?? 0, details: section.duties?.details },
                        { label: t`Skills Mentioned`, score: section.skills?.score ?? 0, details: section.skills?.details },
                        { label: t`Operations`, score: section.operations?.score ?? 0, details: section.operations?.details },
                      ];

                      return (
                        <div key={idx} className="rounded-md border bg-secondary/30 p-2.5 space-y-2">
                          <p className="font-medium text-xs">{section.name} {t`Section`}</p>
                          <ol className="space-y-1.5">
                            {rubricRows.map((row, rowIdx) => (
                              <li key={rowIdx} className="space-y-0.5">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs">{rowIdx + 1}. {row.label}</span>
                                  <span className={`font-bold text-xs ${getScoreColor(row.score)}`}>
                                    {row.score}%
                                  </span>
                                </div>
                                {row.details && (
                                  <p className="pl-4 text-[11px] text-muted-foreground leading-relaxed">
                                    {row.details}
                                  </p>
                                )}
                              </li>
                            ))}
                            <li className="space-y-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs">5. {t`Repetition happening`}</span>
                                <span className={`font-bold text-xs ${getScoreColor(100 - (section.repetition?.score ?? 0))}`}>
                                  {section.repetition?.score ?? 0}%
                                </span>
                              </div>
                              {section.repetition?.details && (
                                <p className="pl-4 text-[11px] text-muted-foreground leading-relaxed">
                                  {section.repetition.details}
                                </p>
                              )}
                              {section.repetition?.highlights?.length > 0 && (
                                <div className="pl-4 space-y-1">
                                  {section.repetition.highlights.map((highlight, highlightIdx) => (
                                    <p
                                      key={highlightIdx}
                                      className="rounded bg-orange-50 dark:bg-orange-950/30 px-2 py-1 text-[11px] leading-relaxed"
                                    >
                                      "{highlight}"
                                    </p>
                                  ))}
                                </div>
                              )}
                            </li>
                          </ol>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-xs text-muted-foreground">{t`No section-level repetition analysis available`}</p>
                  )}
                </div>

                {/* 4. Suggestions if any */}
                {suggestions.length > 0 && (
                  <div className="rounded-lg border p-3 space-y-2">
                    <h4 className="font-semibold text-sm">{t`Suggestions`}</h4>
                    <ul className="space-y-1.5">
                      {suggestions.map((suggestion, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs">
                          <span className="mt-0.5 text-primary">•</span>
                          <span>{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              );
            })()}

            {/* Check Again Button */}
            <Button
              variant="outline"
              className="w-full"
              size="sm"
              onClick={() => {
                setResult(null);
                setJobDescription("");
                setMainRequirements("");
                if (isHighlighted) clearHighlights();
              }}
            >
              {t`Check Again`}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
